import React, { useState } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
} from 'recharts';

/* ─── Mock data (based on real prod AMBR orders) ─────────────── */
const PAYMENT_DATA = [
  { method: 'Credit/Debit Card', count: 18, pct: 64, color: '#2563eb' },
  { method: 'PayPal',            count:  6, pct: 21, color: '#003087' },
  { method: 'Bank Transfer',     count:  3, pct: 11, color: '#7c3aed' },
  { method: 'Other',             count:  1, pct:  4, color: '#94a3b8' },
];

const STATUS_DATA = [
  { status: 'Completed',           count: 16, pct: 57, color: '#16a34a' },
  { status: 'Awaiting Fulfillment',count:  7, pct: 25, color: '#d97706' },
  { status: 'Awaiting Payment',    count:  3, pct: 11, color: '#f59e0b' },
  { status: 'Refunded',            count:  2, pct:  7, color: '#dc2626' },
];

/* Cross breakdown: status × payment for drill-down table */
const CROSS_DATA = [
  { status: 'Completed',            card: 12, paypal: 3, bank: 1 },
  { status: 'Awaiting Fulfillment', card:  4, paypal: 2, bank: 1 },
  { status: 'Awaiting Payment',     card:  2, paypal: 1, bank: 0 },
  { status: 'Refunded',             card:  0, paypal: 0, bank: 1 },
];

/* ─── Tooltip ───────────────────────────────────────────────── */
const Tip = ({ active, payload }: any) => {
  if (!active || !payload?.length) { return null; }
  return (
    <div style={{ background: '#0f172a', color: '#f8fafc', padding: '0.5rem 0.75rem', borderRadius: 8, fontSize: '0.78rem', boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
      {payload.map((p: any) => (
        <div key={p.name} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.fill || p.color, flexShrink: 0 }} />
          <span style={{ opacity: 0.75 }}>{p.name || p.dataKey}:</span>
          <span style={{ fontWeight: 700 }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ─── Shared card shell ─────────────────────────────────────── */
function Card({ title, sub, children, highlight }: { title: string; sub?: string; children: React.ReactNode; highlight?: boolean }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '1.25rem',
      border: highlight ? '2px solid #2563eb' : '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>{title}</h3>
      {sub && <p style={{ margin: '0 0 0.875rem', fontSize: '0.72rem', color: '#94a3b8' }}>{sub}</p>}
      {children}
    </div>
  );
}

/* ─── Donut with centre label ───────────────────────────────── */
function DonutCard({ title, sub, data, total }: { title: string; sub: string; data: Array<{ count: number; pct: number; color: string; [k: string]: any }>; total: number }) {
  return (
    <Card title={title} sub={sub}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <ResponsiveContainer width={130} height={130}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={38} outerRadius={58} paddingAngle={2} dataKey="count" strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central">
              <tspan x="50%" dy="-0.3em" fontSize="1.3rem" fontWeight="800" fill="#0f172a">{total}</tspan>
              <tspan x="50%" dy="1.3em" fontSize="0.6rem" fill="#94a3b8" fontWeight="600" letterSpacing="0.06em">ORDERS</tspan>
            </text>
            <Tooltip content={<Tip />} />
          </PieChart>
        </ResponsiveContainer>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {data.map((d, i) => (
            <div key={i}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.15rem', fontSize: '0.78rem' }}>
                <span style={{ color: '#374151', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                  {(d as any).method ?? (d as any).status}
                </span>
                <span style={{ color: d.color, fontWeight: 700 }}>{d.count} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({d.pct}%)</span></span>
              </div>
              <div style={{ height: 4, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${d.pct}%`, background: d.color, borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ─── Cross breakdown table ─────────────────────────────────── */
function CrossTable() {
  return (
    <Card title="Status × Payment cross-breakdown" sub="How each order status breaks down by payment type">
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#94a3b8', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#2563eb', fontSize: '0.72rem', textTransform: 'uppercase' }}>Card</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#003087', fontSize: '0.72rem', textTransform: 'uppercase' }}>PayPal</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#7c3aed', fontSize: '0.72rem', textTransform: 'uppercase' }}>Bank</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#0f172a', fontSize: '0.72rem', textTransform: 'uppercase' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {CROSS_DATA.map((row, i) => {
              const statusColor = STATUS_DATA.find(s => s.status === row.status)?.color ?? '#94a3b8';
              const total = row.card + row.paypal + row.bank;
              return (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '0.6rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
                    <span style={{ fontWeight: 500, color: '#334155' }}>{row.status}</span>
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', color: row.card ? '#2563eb' : '#d1d5db', fontWeight: row.card ? 600 : 400 }}>{row.card || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', color: row.paypal ? '#003087' : '#d1d5db', fontWeight: row.paypal ? 600 : 400 }}>{row.paypal || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', color: row.bank ? '#7c3aed' : '#d1d5db', fontWeight: row.bank ? 600 : 400 }}>{row.bank || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─── Layout option selector ────────────────────────────────── */
type LayoutOpt = 'A' | 'B' | 'C';

const LAYOUT_OPTS: Array<{ id: LayoutOpt; label: string; desc: string; rec?: boolean }> = [
  { id: 'A', label: 'Side by side (3rd row)', desc: 'Payment + Status as a new row below existing charts. Keeps dashboard linear.', rec: true },
  { id: 'B', label: 'Expanded 3-col middle',  desc: 'Donut + Companions + Status/Payment in a 3-column middle row. Denser.' },
  { id: 'C', label: '+ Cross breakdown table', desc: 'Option A plus a cross-breakdown table showing Status × Payment combinations.' },
];

/* ─── Stub top section (KPIs + existing charts) ─────────────── */
function StubTop() {
  return (
    <>
      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        {[
          { label: 'AMBR Units Sold', value: 28, color: '#2563eb' },
          { label: 'Standalone', value: 3, color: '#16a34a' },
          { label: 'Bundled', value: 25, color: '#f59e0b' },
          { label: 'Countries', value: 17, color: '#7c3aed' },
        ].map((k, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 10, padding: '0.875rem', border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: k.color }} />
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.65rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{k.label}</p>
            <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800, color: k.color, lineHeight: 1 }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Existing middle row stub */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>Purchase Mix</p>
          <div style={{ background: '#f1f5f9', borderRadius: '50%', width: 80, height: 80, margin: '0 auto 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>28</span>
          </div>
          <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>Donut chart</p>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0' }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>Top Bundle Companions</p>
          {['CFA Program Level I', 'Level I Registration', 'Level I Digital Curriculum'].map((name, i) => (
            <div key={i} style={{ marginBottom: '0.4rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: '0.15rem' }}>
                <span style={{ color: '#374151' }}>{name}</span>
                <span style={{ color: '#2563eb', fontWeight: 600 }}>{12 - i * 2}</span>
              </div>
              <div style={{ height: 4, background: '#f1f5f9', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${(12 - i * 2) / 12 * 100}%`, background: '#2563eb', borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Existing bottom row stub */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0' }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>Daily Acquisition Trend</p>
          <div style={{ height: 80, background: '#f8fafc', borderRadius: 8, display: 'flex', alignItems: 'flex-end', gap: 4, padding: '0.5rem' }}>
            {[2, 8, 8, 7, 3].map((v, i) => (
              <div key={i} style={{ flex: 1, background: '#2563eb', borderRadius: '3px 3px 0 0', height: `${v / 8 * 100}%`, opacity: 0.7 + i * 0.06 }} />
            ))}
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0' }}>
          <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#0f172a' }}>Geographic Reach</p>
          {['United States (8)', 'United Kingdom (3)', 'Indonesia (2)'].map((c, i) => (
            <div key={i} style={{ marginBottom: '0.4rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', marginBottom: '0.15rem' }}>
                <span style={{ color: '#374151' }}>{c}</span>
              </div>
              <div style={{ height: 4, background: '#f1f5f9', borderRadius: 999 }}>
                <div style={{ height: '100%', width: `${[100,37,25][i]}%`, background: '#2563eb', borderRadius: 999 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */
export function AMBRPaymentStatusMockup() {
  const [layout, setLayout] = useState<LayoutOpt>('A');

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif", background: '#f1f5f9', minHeight: '100vh', padding: '2rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>

        {/* Page header */}
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.4rem', letterSpacing: '-0.02em' }}>
          AMBR Dashboard — Payment & Status Breakdown
        </h1>
        <p style={{ color: '#64748b', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Mockup showing where Payment Type and Order Status analytics would fit. Toggle between layout options below.
        </p>

        {/* Layout toggle */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          {LAYOUT_OPTS.map(opt => (
            <button key={opt.id} onClick={() => setLayout(opt.id)} style={{
              padding: '0.625rem 1.25rem', borderRadius: 10, cursor: 'pointer',
              border: layout === opt.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
              background: layout === opt.id ? '#eff6ff' : '#fff',
              color: layout === opt.id ? '#2563eb' : '#475569',
              fontWeight: layout === opt.id ? 700 : 500,
              fontSize: '0.875rem', transition: 'all 0.15s',
            }}>
              {opt.id === 'A' && '⭐ '}Option {opt.id} — {opt.label}
            </button>
          ))}
        </div>

        {/* Selected layout description */}
        <div style={{ background: layout === 'A' ? '#eff6ff' : '#f8fafc', border: `1px solid ${layout === 'A' ? '#bfdbfe' : '#e2e8f0'}`, borderRadius: 10, padding: '0.875rem 1.25rem', marginBottom: '1.5rem', fontSize: '0.85rem', color: layout === 'A' ? '#1d4ed8' : '#475569' }}>
          {LAYOUT_OPTS.find(o => o.id === layout)?.desc}
        </div>

        {/* Dashboard preview */}
        <div style={{ background: '#f8fafc', borderRadius: 16, border: '2px solid #e2e8f0', padding: '1.25rem', boxShadow: '0 4px 16px rgba(0,0,0,0.06)' }}>

          {/* Page title stub */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.25rem', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>AMBR Purchase Intelligence</h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>Associate Membership · May 26–29</p>
            </div>
            <span style={{ background: '#16a34a', color: '#fff', fontSize: '0.65rem', padding: '0.2rem 0.6rem', borderRadius: 999, fontWeight: 700 }}>LIVE · PROD</span>
          </div>

          {/* Existing content stub */}
          <StubTop />

          {/* ── NEW SECTION (varies by layout) ── */}
          {layout === 'A' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 0.75rem' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 0.5rem' }}>New sections</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
                <DonutCard
                  title="By Payment Type"
                  sub="How AMBR orders were paid · click to drill in"
                  data={PAYMENT_DATA}
                  total={28}
                />
                <DonutCard
                  title="By Order Status"
                  sub="Fulfillment status of AMBR orders"
                  data={STATUS_DATA}
                  total={28}
                />
              </div>
            </div>
          )}

          {layout === 'B' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 0.75rem' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 0.5rem' }}>New sections (3-column row)</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.875rem' }}>
                <div style={{ background: '#fff', borderRadius: 10, padding: '1rem', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <p style={{ margin: '0 0 0.25rem', fontSize: '0.8rem', fontWeight: 600 }}>Purchase Mix</p>
                  <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>Existing donut</p>
                </div>
                <DonutCard title="By Payment Type" sub="Payment method split" data={PAYMENT_DATA} total={28} />
                <DonutCard title="By Order Status"  sub="Fulfillment status"  data={STATUS_DATA}  total={28} />
              </div>
            </div>
          )}

          {layout === 'C' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.5rem 0 0.75rem' }}>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 0.5rem' }}>New sections + cross table</span>
                <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem', marginBottom: '0.875rem' }}>
                <DonutCard title="By Payment Type" sub="Click to drill into payment-specific orders" data={PAYMENT_DATA} total={28} />
                <DonutCard title="By Order Status"  sub="Click to see orders by status"              data={STATUS_DATA}  total={28} />
              </div>
              <CrossTable />
            </div>
          )}
        </div>

        {/* Summary */}
        <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '1rem' }}>
          {LAYOUT_OPTS.map(opt => (
            <div key={opt.id} onClick={() => setLayout(opt.id)} style={{
              background: '#fff', borderRadius: 12, padding: '1rem',
              border: layout === opt.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
              cursor: 'pointer',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                <span style={{ fontWeight: 700, fontSize: '0.85rem', color: layout === opt.id ? '#2563eb' : '#0f172a' }}>Option {opt.id}</span>
                {opt.rec && <span style={{ background: '#2563eb', color: '#fff', fontSize: '0.6rem', padding: '0.1rem 0.4rem', borderRadius: 999, fontWeight: 700 }}>RECOMMENDED</span>}
              </div>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5 }}>{opt.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
